/* Browser-side error reporting, through our own backend.
 *
 * Posts uncaught errors, unhandled rejections and GHD.reportClientError calls to
 * `/v1/client-error`. One report per (kind, message) per page session, at most 20
 * per page load, and a failed send is dropped, never re-reported: this file must
 * not be able to cause an error. Off on localhost unless `?report_errors=on`.
 *
 * First in every page's deferred chain and reads nothing as it evaluates, so a
 * throw in any later file is caught; a report raised before `API_BASE_URL` exists
 * waits for DOMContentLoaded. */
(function () {
  window.GHD = window.GHD || {};

  var ENDPOINT = "/v1/client-error";
  var MAX_REPORTS = 20;
  // Mirror the backend schema (ClientErrorIn): over these it answers 422.
  var MAX_MESSAGE = 2000;
  var MAX_STACK = 8000;
  // Replaced by the release stamp at deploy, like every other `__GHD_VERSION__`.
  var VERSION = "__GHD_VERSION__";

  var seen = new Set();
  var sent = 0;
  var pending = [];
  var flushArmed = false;

  var host = location.hostname;
  var isLocal = host === "localhost" || host === "127.0.0.1";
  var forced = /[?&]report_errors=on(?:&|$)/.test(location.search || "");
  var enabled = !isLocal || forced;

  // Two environments, because this file is on both origins. The app shell
  // defines `CONFIG` (dashboard/js/config.js); every www page defines
  // `GHD_APP` (app-config.js) and never loads config.js at all. Reading only
  // `CONFIG` is why www still reported nothing after the reporter was put on
  // its pages: `send` answered false for every report, `queue` held them to
  // DOMContentLoaded, and `flush` dropped the lot — silently, because
  // `reportClientError` reports a queued payload as taken.
  function apiBase() {
    var env = window.CONFIG || window.GHD_APP;
    var base = env && env.API_BASE_URL;
    return typeof base === "string" && base ? base : null;
  }

  function headers() {
    var out = { "Content-Type": "application/json" };
    // The same bearer Api._request sends; optional here, because a report from
    // the login page has no session to carry.
    var app = window.App;
    var token = app && app._session && app._session.access_token;
    if (token) out.Authorization = "Bearer " + token;
    return out;
  }

  /** POST one report; false when the environment it needs is not there yet. */
  function send(payload) {
    var base = apiBase();
    if (!base) return false;
    try {
      var sending = fetch(base + ENDPOINT, {
        method: "POST",
        keepalive: true,
        headers: headers(),
        body: JSON.stringify(payload),
      });
      // A rejected send would otherwise surface as an unhandled rejection —
      // which this file listens for.
      if (sending && typeof sending.catch === "function") sending.catch(function () {});
    } catch (_error) {
      /* never let the reporter be the error */
    }
    return true;
  }

  function flush() {
    var items = pending.splice(0);
    // Still no environment after the whole chain has run: nothing to send with.
    if (!apiBase()) return;
    items.forEach(send);
  }

  // A report raised before config.js and app-config.js have evaluated waits
  // for the end of the deferred chain, which is DOMContentLoaded.
  function queue(payload) {
    pending.push(payload);
    if (flushArmed) return;
    flushArmed = true;
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", flush);
    else setTimeout(flush, 0);
  }

  function isNoise(message, stack) {
    if (/ResizeObserver loop/.test(message)) return true;
    // A cross-origin script's error arrives as exactly this and nothing else.
    if (message === "Script error." || message === "Script error") return true;
    if (/\b(?:chrome|moz|safari|safari-web|ms-browser)-extension:\/\//.test(stack || "")) return true;
    // The WebExtension API, which no page of ours calls. Safari rejects an
    // extension's content script with this and no stack at all, so the
    // origin test above never sees it.
    if (/\bruntime\.(?:sendMessage|connect)\b/.test(message)) return true;
    return false;
  }

  function currentRoute() {
    try {
      var router = GHD.router;
      var path = router && typeof router.current === "function" ? router.current() : location.pathname;
      var segment = String(path || "/")
        .split("?")[0]
        .split("/")
        .filter(Boolean)[0];
      return segment || "root";
    } catch (_error) {
      return "unknown";
    }
  }

  /* Report one error. */
  GHD.reportClientError = function (kind, message, stack, extra) {
    try {
      if (!enabled) return false;
      var text = String(message == null ? "" : message).trim();
      if (extra && extra.context) text = String(extra.context) + ": " + text;
      text = text.slice(0, MAX_MESSAGE);
      if (!text) return false;
      var trace = stack == null ? null : String(stack).slice(0, MAX_STACK);
      if (isNoise(text, trace)) return false;
      var key = kind + "\n" + text;
      if (seen.has(key)) return false;
      if (sent >= MAX_REPORTS) return false;
      seen.add(key);
      sent += 1;
      var payload = {
        message: text,
        stack: trace,
        route: currentRoute(),
        version: VERSION,
        kind: kind,
      };
      if (!send(payload)) queue(payload);
      return true;
    } catch (_error) {
      return false;
    }
  };

  window.addEventListener("error", function (event) {
    var error = event && event.error;
    var message = (error && error.message) || (event && event.message) || "";
    var stack = error && error.stack;
    if (!stack && event && event.filename) {
      stack = message + " (" + event.filename + ":" + event.lineno + ":" + event.colno + ")";
    }
    GHD.reportClientError("error", message, stack, null);
  });

  /* The class an object came from, or "Object" when that is not a class name.
     Taken off the prototype, not `reason.constructor`, which an ordinary object
     can set to any value; the name must also look like an identifier. */
  var CLASS_NAME = /^[A-Za-z_$][A-Za-z0-9_$]{0,39}$/;
  function className(reason) {
    var proto = Object.getPrototypeOf(reason);
    var ctor = proto && Object.prototype.hasOwnProperty.call(proto, "constructor") && proto.constructor;
    var name = ctor && typeof ctor === "function" ? ctor.name : null;
    return typeof name === "string" && CLASS_NAME.test(name) ? name : "Object";
  }

  /* What to call a rejection reason that is not an Error. An object is described
     by its constructor and own key names: never values and never its toString (a
     URL's carries share tokens and OAuth codes). A reason that IS the string
     "[object Object]" is checked for first, so its text is kept. */
  function reasonText(reason) {
    try {
      if (reason === null || typeof reason !== "object") return String(reason);
      return className(reason) + "{" + Object.keys(reason).slice(0, 12).join(",") + "}";
    } catch (_error) {
      // A reason whose constructor or keys cannot be read. It exists, and that
      // it is this strange is the most useful thing left to say about it.
      return "unreadable rejection reason";
    }
  }

  window.addEventListener("unhandledrejection", function (event) {
    var reason = event && event.reason;
    var message = reason && reason.message ? reason.message : reasonText(reason);
    GHD.reportClientError("unhandledrejection", message, reason && reason.stack, null);
  });
})();
